import { useEffect, useState, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import Select, { components } from "react-select";
import type { StylesConfig } from "react-select";
import { GET_INSTITUTES_OPTION } from "../../Querries";
import { CustomDropdownIndicator } from "../../pages/SchoolList";

interface SchoolOption {
  label: string;
  value: string;
  name: string;
}

function InstituteFilter({ value, onChange, className = "", menuIsOpen }: any) {
  const [limit, setLimit] = useState(10);
  const [school, setSchool] = useState<SchoolOption[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState("");

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, refetch } = useQuery(GET_INSTITUTES_OPTION, {
    variables: { page: 1, limit: limit, school_name: inputValue },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.getallschool?.data) {
      const fetchedSchool = data.getallschool.data
        .map((school: any) => {
          if (!school.name) return null;
          return {
            label: `${school.name} (${school._id})`,
            value: school._id,
            name: school.name,
          };
        })
        .filter(Boolean);
      setSchool(fetchedSchool);
      setTotalCount(data.getallschool.pagination?.total);
    }
  }, [data]);

  const handleScroll = (event: any) => {
    const target = event.target as HTMLDivElement;
    if (
      !loading &&
      totalCount !== null &&
      school.length < totalCount &&
      target.scrollHeight - target.scrollTop === target.clientHeight
    ) {
      setLimit((prev) => Math.min(prev + 10, totalCount));
    }
  };

  const debouncedSearch = useCallback(
    (val: string) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(async () => {
        setInputValue(val);
        const res = await refetch({ school_name: val, page: 1, limit: limit });
        if (res?.data?.getallschool?.data) {
          const updatedschool = res.data.getallschool.data
            .map((school: any) => {
              if (!school.name) return null;
              return {
                label: `${school.name} (${school._id})`,
                value: school._id,
                name: school.name,
              };
            })
            .filter(Boolean);
          setSchool(updatedschool);

          setTotalCount(res.data.getallschool.pagination?.total);
        }
      }, 700);
    },
    [limit, refetch]
  );

  const CustomMenuList = (props: any) => {
    const { children } = props;
    return (
      <components.MenuList {...props}>
        <div>
          {children}
          {/* {loading && <div className="py-2 text-center text-sm">Loading…</div>} */}
          {totalCount !== null && school.length >= totalCount && (
            <div className="py-2 text-center text-xs text-gray-500">
              All content loaded.
            </div>
          )}
          {totalCount !== null && school.length < totalCount && !loading && (
            <div className="py-2 text-center text-xs text-gray-500">
              Scroll down to load more...
            </div>
          )}
        </div>
      </components.MenuList>
    );
  };

  const customStyles: StylesConfig<SchoolOption> = {
    control: (base) => ({
      ...base,
      backgroundColor: "#F6F8FA",
      border: "1px solid #1B163B",
      borderRadius: "6px",
      minHeight: "15px",
      margin: 0,
      fontSize: "14px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0px 0.5rem",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "200px",
    }),
    input: (base) => ({
      ...base,
      backgroundColor: "transparent",
      color: "#000",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#1E1B59",
      fontSize: "12px",
    }),
  };

  return (
    <div className="w-full flex flex-col">
      <Select
        className="font-normal capitalize"
        closeMenuOnSelect={true}
        onMenuScrollToBottom={handleScroll}
        onInputChange={(val) => debouncedSearch(val)}
        options={school}
        menuIsOpen={menuIsOpen}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: () => null,
          MenuList: CustomMenuList,
        }}
        onChange={(selected: any) => {
          onChange(selected);
        }}
        placeholder="Select Institute"
        styles={customStyles}
        isMulti={false}
        isLoading={loading}
        value={value}
      />
    </div>
  );
}

export default InstituteFilter;
